import { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  FloatingPortalContainerProvider,
  IslandPortalContainerProvider,
} from "@frontend/contexts";
import { vars } from "@frontend/styles";

import { legacyTheme } from "../styles";
import { componentManager } from "./ComponentManager";
import { BridgeEvents } from "./events";
import type { ComponentHandle, ComponentName, MountPoint } from "./types";

// Collect all bridge stylesheets once at startup
export const bridgeStyleSheet = new CSSStyleSheet();

// Isolates a shadow root's typography from the surrounding page.
export const hostResetStyleSheet = new CSSStyleSheet();

// Why based font-size: 14px? On SmartAdmint legacy theme, the default
// font-size is 14px. This is the only way to ensure that the font-size
// is consistent across all themes and that the rem unit is consistent
// across all themes.
hostResetStyleSheet.replaceSync(`
  :host {
    all: initial;
    font-family: ${vars.font.family.sans};
    font-size: 14px;
    text-size-adjust: auto;
    padding: 0;
    margin: 0;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`);

const STYLE_LINKS =
  'link[data-vanilla-extract], link[rel="stylesheet"][href*="/vanilla-extract/"]';

export function initBridgeStyles(): void {
  const rules = Array.from(
    document.querySelectorAll<HTMLLinkElement>(STYLE_LINKS),
  )
    .flatMap((el) => {
      try {
        return Array.from(el.sheet?.cssRules ?? []).map((r) => r.cssText);
      } catch {
        return [];
      }
    })
    .join("\n");

  bridgeStyleSheet.replaceSync(rules);
}

export function refreshBridgeStyles(): void {
  initBridgeStyles();
}

let nextComponentId = 0;

const stableComponentIds = new WeakMap<HTMLElement, number>();

function stableComponentId(el: HTMLElement): number {
  let id = stableComponentIds.get(el);

  if (id === undefined) {
    id = nextComponentId++;
    stableComponentIds.set(el, id);
  }

  return id;
}

let bridgePortalRoot: ShadowRoot | undefined;

function getBridgePortalRoot(): ShadowRoot {
  if (!bridgePortalRoot) {
    const host = document.createElement("div");

    host.setAttribute("aria-live", "off");
    host.setAttribute("data-rwc-portal", "");

    document.body.appendChild(host);

    bridgePortalRoot = host.attachShadow({ mode: "open" });

    bridgePortalRoot.adoptedStyleSheets = [
      hostResetStyleSheet,
      bridgeStyleSheet,
    ];
  }

  return bridgePortalRoot;
}

// Kept outside the component: the React Compiler forbids mutating a value
// reached through a prop during render, and the shadow root is one.
function attachShadowMount(el: HTMLElement): {
  shadowRoot: ShadowRoot;
  container: HTMLDivElement;
} {
  const shadowRoot = el.shadowRoot ?? el.attachShadow({ mode: "open" });

  shadowRoot.adoptedStyleSheets = [hostResetStyleSheet, bridgeStyleSheet];

  const div = document.createElement("div");

  // Transparent to layout: the host's box (inline by default) should
  // follow whatever the mounted component's own root renders as, not be
  // forced block-level just because this mount point happens to be one.

  div.style.display = "contents";

  shadowRoot.appendChild(div);

  return { shadowRoot, container: div };
}

// Isolated component rendering using Shadow DOM
export function ShadowPortal({
  el,
  componentId,
  children,
}: {
  el: HTMLElement;
  componentId: string;
  children: React.ReactNode;
}) {
  // Lazy init runs synchronously during render (not in an effect after paint),
  // so the portal below mounts in the same commit as the shadow root attachment.

  const [{ shadowRoot, container }] = useState(() => attachShadowMount(el));

  // Components that project their own light-DOM children via a native `<slot>`.
  return createPortal(
    <IslandPortalContainerProvider value={shadowRoot}>
      {children}
    </IslandPortalContainerProvider>,
    container,
    componentId,
  );
}

// Memoized so a change on one mount point doesn't re-render the others
const MountedComponent = memo(function MountedComponent({
  el,
  componentId,
  componentName,
  props,
  registerHandle,
  unregisterHandle,
}: {
  el: HTMLElement;
  componentId: string;
  componentName: ComponentName;
  props: Record<string, unknown>;
  registerHandle: (
    id: string,
    componentName: ComponentName,
    handle: ComponentHandle<ComponentName>,
    el: HTMLElement,
  ) => void;
  unregisterHandle: (id: string, el: HTMLElement) => void;
}) {
  const Component = componentManager.getDefinition(componentName)?.component;

  const handleRef = useCallback(
    (handle: ComponentHandle<typeof componentName>) => {
      if (handle) {
        registerHandle(componentId, componentName, handle, el);
      } else {
        unregisterHandle(componentId, el);
      }
    },
    [componentId, componentName, el, registerHandle, unregisterHandle],
  );

  if (!Component) {
    return null;
  }

  return (
    <ShadowPortal el={el} componentId={componentId}>
      <Component {...props} ref={handleRef} />
    </ShadowPortal>
  );
});

export function BridgeContent({ mountPoints }: { mountPoints: MountPoint[] }) {
  const handleRefs = useRef<Map<string, unknown>>(new Map());
  const [portalRoot] = useState(getBridgePortalRoot);

  // Apply the legacy theme once on mount
  useEffect(() => {
    document.documentElement.classList.add(legacyTheme);

    return () => document.documentElement.classList.remove(legacyTheme);
  }, []);

  const registerHandle = useCallback(
    <T extends ComponentName>(
      id: string,
      _componentName: T,
      handle: ComponentHandle<T>,
      el: HTMLElement,
    ) => {
      if (!handle) {
        return;
      }

      handleRefs.current.set(id, handle);

      // Also store in the global registry shared with legacy code (see
      // Bridge.tsx for the Window.ReactComponents declaration).

      window.ReactComponents[id] = handle;

      // Copy the handle's methods directly onto the custom element itself,
      // so callers can do `document.getElementById(id).someMethod()` instead
      // of going through window.ReactComponents[id].

      Object.assign(el, handle);

      // Emit an event to notify that the component is ready (with its handle)
      window.dispatchEvent(
        BridgeEvents.componentReady.create({
          id,
          handle,
        }),
      );
    },
    [],
  );

  const unregisterHandle = useCallback((id: string, el: HTMLElement) => {
    const handle = handleRefs.current.get(id);

    // Remove the methods copied onto the element by registerHandle
    if (handle && typeof handle === "object") {
      Object.keys(handle).forEach((key) => {
        delete (el as unknown as Record<string, unknown>)[key];
      });
    }

    handleRefs.current.delete(id);

    delete window.ReactComponents[id];
  }, []);

  return (
    <FloatingPortalContainerProvider value={portalRoot}>
      {mountPoints.map(({ el, componentName, props }) => {
        // Generate a unique key for each portal or use existing id
        const componentId =
          el.dataset.id || `${componentName}-${stableComponentId(el)}`;

        return (
          <MountedComponent
            key={componentId}
            el={el}
            componentId={componentId}
            componentName={componentName}
            props={props}
            registerHandle={registerHandle}
            unregisterHandle={unregisterHandle}
          />
        );
      })}
    </FloatingPortalContainerProvider>
  );
}
